import axios from 'axios';
import { API_BASE_URL } from './utils';

const API_URL = API_BASE_URL;

const STAFF_BASE = "/admin/chat";
const CUSTOMER_BASE = "/mobile/chat";

const chatApi = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

function pickAuthToken() {
  const staffToken = localStorage.getItem('company_admin_token');
  const customerToken = localStorage.getItem('customer_token');

  // If both exist (common in automated tests), choose based on current panel route.
  try {
    const path = window?.location?.pathname || '';
    if (path.startsWith('/customers')) return customerToken || staffToken;
    if (path.startsWith('/admin')) return staffToken || customerToken;
  } catch (e) {
    // ignore
  }

  return staffToken || customerToken;
}

function pickChatBase() {
  try {
    const path = window?.location?.pathname || '';
    if (path.startsWith('/customers')) return CUSTOMER_BASE;
    if (path.startsWith('/admin')) return STAFF_BASE;
  } catch (e) {
    // ignore
  }
  return STAFF_BASE;
}

chatApi.interceptors.request.use((config) => {
  const token = pickAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const realtimeChatAPI = {
  // Staff
  staffGroups: () => chatApi.get(`${STAFF_BASE}/groups`),
  staffGroupsWithUnread: () => chatApi.get(`${STAFF_BASE}/groups-with-unread`),
  staffCustomers: (groupId) => chatApi.get(`${STAFF_BASE}/customers`, { params: { group_id: groupId } }),
  staffThreadForCustomer: (customerId) => chatApi.post(`${STAFF_BASE}/thread-for-customer`, { customer_id: customerId }),
  staffUnreadTotal: () => chatApi.get(`${STAFF_BASE}/unread`),

  // Customer
  customerThread: () => chatApi.get(`${CUSTOMER_BASE}/thread`),
  customerUnread: () => chatApi.get(`${CUSTOMER_BASE}/thread/unread`),

  // Messages
  listMessages: (threadId, params = {}) => chatApi.get(`${pickChatBase()}/threads/${threadId}/messages`, { params }),
  sendMessage: (threadId, payload) => chatApi.post(`${pickChatBase()}/threads/${threadId}/messages`, payload),
  markRead: (threadId, lastReadMessageId) => chatApi.post(`${pickChatBase()}/threads/${threadId}/read`, { last_read_message_id: lastReadMessageId }),
  blockThread: (threadId, blocked) => chatApi.post(`${pickChatBase()}/threads/${threadId}/block`, { blocked }),
  deleteThread: (threadId) => chatApi.delete(`${pickChatBase()}/threads/${threadId}`),

  deleteForMe: (threadId, messageId) =>
    chatApi.post(`${pickChatBase()}/messages/${messageId}/delete-for-me`, { thread_id: threadId }),
  deleteForAll: (threadId, messageId) =>
    chatApi.post(`${pickChatBase()}/messages/${messageId}/delete-for-all`, { thread_id: threadId }),

  // Single-file upload (same as backend POST /upload). kind: 'image' | 'file' | 'audio'
  uploadMedia: (file, kind = 'file') => {
    const form = new FormData();
    form.append('file', file);
    form.append('kind', kind);
    return chatApi.post(`${pickChatBase()}/upload`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Uploads (chunked) – legacy
  uploadInit: (payload) => chatApi.post(`${pickChatBase()}/uploads/init`, payload),
  uploadChunk: (uploadId, index, chunkBytes) =>
    chatApi.put(`${pickChatBase()}/uploads/${uploadId}/chunk`, chunkBytes, {
      params: { index },
      headers: { 'Content-Type': 'application/octet-stream' },
    }),
  uploadComplete: (uploadId, totalChunks) =>
    chatApi.post(`${pickChatBase()}/uploads/${uploadId}/complete`, { total_chunks: totalChunks }),

  mediaUrl: (mediaId) => {
    const token = pickAuthToken();
    const base = `${API_URL}${pickChatBase()}/media/${mediaId}`;
    if (!token) return base;
    return `${base}?token=${encodeURIComponent(token)}`;
  },
};

export default chatApi;
